import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, limit as firestoreLimit } from 'firebase/firestore';
import { firestore } from '../../../services/startFirestore';
import type { FormSplitConfiguration } from '#types/System.types';
import type { ArRowData } from '#types/System.Artifacts.types';
import { AR_TABLE_AGGREGATIONS_ROW_ID, arTableRowsPath } from '#types/System.Artifacts.types';
import { FORM_SPLIT_AR_TABLE_ROWS_LIMIT } from '#types/System.Parameters.types';

type UseArTableRowsForSplitParams = {
  splitConfig: FormSplitConfiguration | undefined;
  arTableParentPath: string | undefined;
  rowLimit?: number;
  enabled?: boolean;
};

type UseArTableRowsForSplitResult = {
  rowsByTableId: Record<string, readonly ArRowData[]>;
  splitRowLimitWarning: { tableIds: readonly string[]; rowLimit: number } | null;
};

function useArTableRowsForSplit({
  splitConfig,
  arTableParentPath,
  rowLimit = FORM_SPLIT_AR_TABLE_ROWS_LIMIT,
  enabled = false,
}: UseArTableRowsForSplitParams): UseArTableRowsForSplitResult {
  const [rowsByTableId, setRowsByTableId] = useState<Record<string, readonly ArRowData[]>>({});
  const [rowLimitReachedByTableId, setRowLimitReachedByTableId] = useState<Record<string, boolean>>({});

  const tableIdsToBind = useMemo(() => {
    if (!splitConfig?.enabled || !arTableParentPath) return [];
    const splitBy = splitConfig?.splitBy ?? [];
    return Array.from(new Set(splitBy.map((r) => r.fieldId)));
  }, [arTableParentPath, splitConfig?.enabled, splitConfig?.splitBy]);

  useEffect(() => {
    if (!enabled) return;

    if (!splitConfig?.enabled || !arTableParentPath || tableIdsToBind.length === 0) return;

    const unsubs = tableIdsToBind.map((tableId) => {
      const path = arTableRowsPath(arTableParentPath, tableId);
      const q = query(collection(firestore, path), firestoreLimit(rowLimit));

      return onSnapshot(q, (snap) => {
        const rows = snap.docs
          .filter((d) => d.id !== AR_TABLE_AGGREGATIONS_ROW_ID)
          .map((d) => d.data() as ArRowData);
        setRowsByTableId((prev) => ({ ...prev, [tableId]: rows }));
        setRowLimitReachedByTableId((prev) => ({ ...prev, [tableId]: snap.size >= rowLimit }));
      }, (error: unknown) => {
        console.error(`[useArTableRowsForSplit] Failed to bind rows for ${path}`, error);
        setRowsByTableId((prev) => ({ ...prev, [tableId]: [] }));
        setRowLimitReachedByTableId((prev) => ({ ...prev, [tableId]: false }));
      });
    });

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [arTableParentPath, enabled, rowLimit, splitConfig?.enabled, tableIdsToBind]);

  const derivedRowsByTableId = useMemo(() => {
    if (!enabled) return {};
    if (tableIdsToBind.length === 0) return {};

    return Object.fromEntries(
      tableIdsToBind.map((tableId) => [tableId, rowsByTableId[tableId] ?? []] as const)
    ) as Record<string, readonly ArRowData[]>;
  }, [enabled, rowsByTableId, tableIdsToBind]);

  const tableIdsReachedRowLimit = useMemo(() => {
    if (!enabled) return [];
    if (tableIdsToBind.length === 0) return [];
    return tableIdsToBind.filter((tableId) => (rowLimitReachedByTableId[tableId] ?? false) === true);
  }, [enabled, rowLimitReachedByTableId, tableIdsToBind]);

  const splitRowLimitWarning =
    tableIdsReachedRowLimit.length > 0 ? { tableIds: tableIdsReachedRowLimit, rowLimit } : null;

  return { rowsByTableId: derivedRowsByTableId, splitRowLimitWarning };
}

export { useArTableRowsForSplit };

